/**
 * ScrollIndicator hook - Shows floating indicators when there's more content to scroll
 *
 * This hook monitors the scroll position of a horizontally scrollable container
 * and shows/hides right-arrow indicators when there's more content to the right.
 *
 * The hook should be attached to a wrapper element that contains:
 * - .scroll-indicator - One or more indicator elements to show/hide
 * - .scroll-container - The scrollable container to monitor
 */
export const ScrollIndicator = {
  mounted() {
    this.indicators = this.el.querySelectorAll(".scroll-indicator")
    this.scrollContainer = this.el.querySelector(".scroll-container")
    this.checkScroll = this.checkScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)

    if (!this.scrollContainer) {
      console.warn("ScrollIndicator: No scroll container found")
      return
    }

    // Check initial state
    this.checkScroll()

    // Listen for scroll and resize events
    this.scrollContainer.addEventListener("scroll", this.checkScroll, {
      passive: true,
    })
    window.addEventListener("resize", this.handleResize, { passive: true })

    // Also check after content might have loaded (charts, etc.)
    setTimeout(() => this.checkScroll(), 100)
    setTimeout(() => this.checkScroll(), 500)
    setTimeout(() => this.checkScroll(), 1000)
  },

  updated() {
    // Recheck when component updates
    this.checkScroll()
  },

  destroyed() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener("scroll", this.checkScroll)
    }
    window.removeEventListener("resize", this.handleResize)
  },

  handleResize() {
    this.checkScroll()
  },

  checkScroll() {
    if (!this.scrollContainer) return

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer
    const hasMoreContent = scrollWidth > clientWidth
    const isNearEnd = scrollLeft + clientWidth >= scrollWidth - 20 // 20px threshold

    const shouldShow = hasMoreContent && !isNearEnd

    this.indicators.forEach((indicator) => {
      if (shouldShow) {
        indicator.classList.remove("hidden")
      } else {
        indicator.classList.add("hidden")
      }
    })
  },
}
